import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { getFeatureFlags } from "@api/getFeatureFlags";
import { ORGANIZATION_KEY } from "@constants";
import { useUser } from "@hooks/useUser";
import { OrgData } from "@types";
import { recoverBrowserSecurityError } from "@utils/storage";
interface IOrgContext {
  org?: OrgData;
  setOrg: (org: OrgData) => void;
}

export const OrgContext = React.createContext<IOrgContext>({
  setOrg: () => {
    throw new Error("OrgContext not initialized");
  },
});

interface OrgProviderProps {
  setOrgFeaturesLoaded: (loaded: boolean) => void;
  setPermissionsLoaded: (loaded: boolean) => void;
}

export const OrgProvider: React.FC<OrgProviderProps> = ({
  setOrgFeaturesLoaded,
  setPermissionsLoaded,
  children,
}) => {
  const history = useHistory();
  const user = useUser();
  const [org, setOrg] = useState<OrgData>();
  const username = user?.username;

  const firstOrg = user?.orgs.sort(
    (a: OrgData, b: OrgData) => parseInt(b.id) - parseInt(a.id)
  )[0];

  const path = history.location.pathname;
  const url_org_name =
    path.startsWith("/orgs/") && path.split("/").length > 2
      ? path.split("/")[2]
      : undefined;

  const syncOrgFeatureFlags = async (org: OrgData) => {
    setOrgFeaturesLoaded(false);
    try {
      const resp = await getFeatureFlags(org.id);
      org!.features = resp.features;
      org!.has_rbac = resp.has_rbac;
      org!.has_autofix = resp.has_autofix;
      org!.editor_semgrep_version = resp.editor_semgrep_version;
      setOrg(org);
      recoverBrowserSecurityError(() =>
        localStorage.setItem(ORGANIZATION_KEY, JSON.stringify(org))
      );
    } catch (error) {
      // nosemgrep
      console.error(error);
    } finally {
      setOrgFeaturesLoaded(true);
      setPermissionsLoaded(false);
    }
  };

  const loadOrg = async () => {
    if (!username || org) return;
    setOrgFeaturesLoaded(false);
    const savedOrgData = recoverBrowserSecurityError(() =>
      localStorage.getItem(ORGANIZATION_KEY)
    );
    let savedOrg: OrgData | undefined = undefined;
    let newOrg: OrgData | undefined;
    if (savedOrgData) {
      savedOrg = JSON.parse(savedOrgData);
    }
    if (url_org_name && user?.orgs.some((o) => o.name === url_org_name)) {
      // If URL specifies an org, load that org
      newOrg = user?.orgs.find((o) => o.name === url_org_name);
    } else if (user?.orgs.some((o) => o.id === savedOrg?.id || 0)) {
      // If URL does not successfully load an org, use saved org data
      newOrg = savedOrg;
    } else if (firstOrg) {
      // In absence of saved org, use most-recently-added org
      newOrg = firstOrg;
    }

    // load feature flags for org from backend
    if (newOrg) {
      await syncOrgFeatureFlags(newOrg);
      // do not call setOrgFeaturesLoaded in this case until promise resolves
      // (it is done in the finally clause of syncOrg)
    } else {
      await setOrgFeaturesLoaded(true);
    }
  };

  useEffect(() => {
    // Load user and org from local storage at start-up
    loadOrg();
    // eslint-disable-next-line
  }, []); // This must remain empty so the useEffect only runs once.

  return (
    <OrgContext.Provider
      value={{
        org,
        setOrg: syncOrgFeatureFlags,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};
